import { useUser } from '@clerk/nextjs';
import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import type { AvailabilityReminder } from '@web-app/server/services/availability/availability-reminder.utils';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

type ReminderSurface = 'appointments-banner' | 'modal';

/** Log deliberate Calendar clicks separately from reminder diagnostics. */
export function useAvailabilityReminderTracking(
  reminderSurface: ReminderSurface,
) {
  const { user } = useUser();
  const { route } = useRouter();
  const userId = user?.id;
  const healthieUserId = user?.publicMetadata?.healthie_user_id;
  const providerId =
    typeof healthieUserId === 'number' || typeof healthieUserId === 'string'
      ? String(healthieUserId)
      : undefined;

  return useCallback(
    (reminder: AvailabilityReminder) => {
      try {
        datadogLogs.logger.info(
          'Availability reminder update clicked',
          logContext('availability', 'reminder-update-click', {
            detail:
              'Provider chose to open Calendar from an availability reminder',
            userId,
            providerId,
            reminderSurface,
            // Route patterns avoid including patient IDs or query parameters.
            route,
            destination: '/calendar',
            reminderReason: reminder.noAvailability
              ? 'no-availability'
              : 'below-commitment',
            weeklyCommitmentHours: reminder.weeklyCommitmentHours,
          }),
        );
      } catch {
        // Telemetry must not prevent navigation or dismissing the modal.
      }
    },
    [userId, providerId, reminderSurface, route],
  );
}
